"use client";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import {
  getStartupCount,
  getUnicornTimeSeries,
} from "@/services/api/UnicornTracker";
import { Info } from "@phosphor-icons/react";
import CustomTooltip from "@/components/CustomTooltip";

// Map long region names to short abbreviations
const REGION_SHORT_NAMES = {
  "All MEAIPT": "All MEAIPT",
  "ALL MEAIPT": "All MEAIPT",
  "Gulf Cooperation Council": "GCC",
  "Middle East & Africa": "MEA",
  "Middle East and Africa": "MEA",
  "Middle East & North Africa": "MENA",
  "Middle East and North Africa": "MENA",
  "South Asia": "South Asia",
  Levant: "Levant",
  "Middle East": "Middle East",
  "Sub-Saharan Africa": "SSA",
  Africa: "Africa",
};

// Get short name for a region
const getShortRegionName = (name) => {
  if (!name) return "All MEAIPT";
  return REGION_SHORT_NAMES[name] || name;
};

// Format valuation with appropriate unit (M or B)
const formatBillionUSD = (value) => {
  if (!value || value === 0) return null;
  const billions = value / 1_000_000_000;
  if (billions >= 100) return `$${billions.toFixed(0)}B`;
  if (billions >= 1) return `$${billions.toFixed(1)}B`;
  const millions = value / 1_000_000;
  if (millions >= 1) return `$${millions.toFixed(0)}M`;
  return `$${(value / 1_000).toFixed(0)}K`;
};

export default function UnicornDonut({
  selectedCountryName,
  selectedRegionName,
  unicornStats,
  isLoading: statsLoading,
}) {
  const [loading, setLoading] = useState(true);
  const [selectedCount, setSelectedCount] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [selectionLabel, setSelectionLabel] = useState("All MEAIPT");
  const [subtext, setSubtext] = useState("");
  const [unicornBirths, setUnicornBirths] = useState(null);
  const [birthsLoading, setBirthsLoading] = useState(true);
  const allRegionId = "1";

  const searchParams = useSearchParams();
  const selectedRegionId = searchParams.get("unicorn_regions");
  const selectedCountryId = searchParams.get("country");
  const sectors = searchParams.get("sectors");
  const stages = searchParams.get("stages");
  const valuationCondition = searchParams.get("valuation_condition");
  const valuationAmount = searchParams.get("valuation_amount");
  const yearFoundedCondition = searchParams.get("year_founded_condition");
  const yearFoundedYear = searchParams.get("year_founded_year");
  const timeToUnicornMin = searchParams.get("min_time_to_unicorn");
  const timeToUnicornMax = searchParams.get("max_time_to_unicorn");
  const unicornStatus = searchParams.get("unicorn_status");
  useEffect(() => {
    let isActive = true;

    async function fetchData() {
      try {
        setLoading(true);

        const baseParams = {
          is_unicorn: true,
          sectors: sectors || undefined,
          stages: stages || undefined,
          valuation_condition: valuationCondition || undefined,
          valuation_amount: valuationAmount || undefined,
          year_founded_condition: yearFoundedCondition || undefined,
          year_founded_year: yearFoundedYear || undefined,
          min_time_to_unicorn: timeToUnicornMin || undefined,
          max_time_to_unicorn: timeToUnicornMax || undefined,
        };

        let fetchSelectedParams = { ...baseParams };
        let fetchParentParams = { ...baseParams };

        {
          const statuses = unicornStatus
            ? unicornStatus.split(",").filter(Boolean)
            : ["current", "fallen", "alumni"];
          fetchSelectedParams.unicorn_status = statuses.join(",");
          fetchParentParams.unicorn_status = statuses.join(",");
          if (statuses.includes("fallen")) {
            fetchSelectedParams.include_lost_unicorn = 1;
            fetchParentParams.include_lost_unicorn = 1;
          }
          if (statuses.includes("alumni")) {
            fetchSelectedParams.include_alumni = 1;
            fetchParentParams.include_alumni = 1;
          }
          if (!statuses.includes("current")) {
            fetchSelectedParams.exclude_current_unicorn = 1;
            fetchParentParams.exclude_current_unicorn = 1;
          }
        }
        let currentParentLabel = "All MEAIPT";
        let currentSelectionLabel = "All MEAIPT";
        const hasCountry = Boolean(selectedCountryId);
        const hasRegion = Boolean(selectedRegionId);
        const isAllRegion =
          !selectedRegionId || selectedRegionId === allRegionId;
        const selectedRegionLabel = selectedRegionName
          ? getShortRegionName(selectedRegionName)
          : "Region";

        if (!hasCountry && isAllRegion) {
          fetchSelectedParams.unicorn_regions = allRegionId;
          fetchParentParams.unicorn_regions = allRegionId;
          currentSelectionLabel =
            getShortRegionName(selectedRegionName) || "All MEAIPT";
          currentParentLabel = "All MEAIPT";
        } else if (!hasCountry && hasRegion && !isAllRegion) {
          fetchSelectedParams.unicorn_regions = selectedRegionId;
          currentSelectionLabel = selectedRegionLabel;
          fetchParentParams.unicorn_regions = selectedRegionId;
          currentParentLabel = currentSelectionLabel;
        } else if (hasCountry && isAllRegion) {
          fetchSelectedParams.countries = selectedCountryId;
          fetchSelectedParams.unicorn_regions = allRegionId;
          fetchParentParams.unicorn_regions = allRegionId;
          currentSelectionLabel = selectedCountryName || "Country";
          currentParentLabel = "All MEAIPT";
        } else if (hasCountry && hasRegion && !isAllRegion) {
          fetchSelectedParams.countries = selectedCountryId;
          fetchSelectedParams.unicorn_regions = selectedRegionId;
          currentSelectionLabel = selectedCountryName || "Country";

          fetchParentParams.unicorn_regions = selectedRegionId;
          currentParentLabel = selectedRegionLabel;
        }

        if (!isActive) return;
        setSelectionLabel(currentSelectionLabel);

        const [selectedRes, parentRes] = await Promise.all([
          getStartupCount({ params: fetchSelectedParams }),
          getStartupCount({ params: fetchParentParams }),
        ]);

        if (!isActive) return;
        const sCount = selectedRes.data?.count || 0;
        let pCount = parentRes.data?.count || 0;

        if (sCount > pCount) {
          console.error(
            "Data anomaly: Selected unicorn count > Parent unicorn count. Clamping parent count.",
          );
          pCount = sCount;
        }

        setSelectedCount(sCount);

        let percent = 0;
        if (pCount > 0) {
          percent = (sCount / pCount) * 100;
        }

        let formattedPercent = "0%";
        if (pCount > 0) {
          formattedPercent =
            percent < 10 && percent > 0
              ? `${percent.toFixed(1)}%`
              : `${Math.round(percent)}%`;
        }

        setSubtext(
          pCount === 0
            ? "No unicorns in this scope"
            : `${formattedPercent} of ${currentParentLabel}`,
        );

        if (pCount === 0) {
          setChartData([
            { name: "Selected", y: 0, color: "#FF9F43" },
            { name: "Remainder", y: 1, color: "#333333" },
          ]);
        } else {
          const remainder = Math.max(0, pCount - sCount);
          setChartData([
            { name: "Selected", y: sCount, color: "#FF9F43" },
            { name: "Remainder", y: remainder, color: "#333333" },
          ]);
        }
      } catch (error) {
        console.error("Error fetching unicorn counts:", error);
      } finally {
        if (isActive) setLoading(false);
      }
    }

    fetchData();

    return () => {
      isActive = false;
    };
  }, [
    selectedRegionId,
    selectedCountryId,
    selectedCountryName,
    selectedRegionName,
    sectors,
    stages,
    valuationCondition,
    valuationAmount,
    yearFoundedCondition,
    yearFoundedYear,
    timeToUnicornMin,
    timeToUnicornMax,
    unicornStatus,
  ]);

  const options = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      height: "100%",
      spacing: [0, 0, 0, 0],
      margin: [0, 0, 0, 0],
    },
    title: {
      text: null,
    },
    tooltip: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        innerSize: "85%",
        size: "100%",
        center: ["50%", "50%"],
        borderWidth: 0,
        dataLabels: { enabled: false },
        states: {
          hover: { enabled: false },
          inactive: { opacity: 1 },
        },
      },
    },
    credits: { enabled: false },
    series: [
      {
        data: chartData,
        enableMouseTracking: false,
      },
    ],
  };

  useEffect(() => {
    let isActive = true;

    async function fetchBirths() {
      try {
        setBirthsLoading(true);
        const params = { period: "yearly", years: 5, metric: "both" };
        if (selectedRegionId) params.unicorn_regions = selectedRegionId;
        if (selectedCountryId) params.countries = selectedCountryId;
        if (sectors) params.sectors = sectors;
        if (stages) params.stages = stages;
        if (valuationCondition && valuationAmount) {
          params.valuation_condition = valuationCondition;
          params.valuation_amount = valuationAmount;
        }
        if (yearFoundedCondition && yearFoundedYear) {
          params.year_founded_condition = yearFoundedCondition;
          params.year_founded_year = yearFoundedYear;
        }
        if (timeToUnicornMin) params.min_time_to_unicorn = timeToUnicornMin;
        if (timeToUnicornMax) params.max_time_to_unicorn = timeToUnicornMax;
        const statuses = unicornStatus
          ? unicornStatus.split(",").filter(Boolean)
          : ["current", "fallen", "alumni"];
        params.unicorn_status = statuses.join(",");
        if (statuses.includes("fallen")) params.include_lost_unicorn = 1;
        if (statuses.includes("alumni")) params.include_alumni = 1;
        if (!statuses.includes("current")) params.exclude_current_unicorn = 1;
        const response = await getUnicornTimeSeries({ params });
        if (!isActive) return;
        const births = response.data?.data?.unicorn_births_last_12_months;
        setUnicornBirths(births !== undefined ? births : null);
      } catch {
        if (isActive) setUnicornBirths(null);
      } finally {
        if (isActive) setBirthsLoading(false);
      }
    }
    fetchBirths();

    return () => {
      isActive = false;
    };
  }, [
    selectedRegionId,
    selectedCountryId,
    sectors,
    stages,
    valuationCondition,
    valuationAmount,
    yearFoundedCondition,
    yearFoundedYear,
    timeToUnicornMin,
    timeToUnicornMax,
    unicornStatus,
  ]);

  // Valuation data from unicornStats prop
  const totalValuation = unicornStats?.total_valuation ?? 0;
  const totalUnicorns = unicornStats?.total_unicorn || 0;
  const hasUnicorns = totalUnicorns > 0;
  const hasValuationData = totalValuation > 0;
  const showValuationNA = !statsLoading && hasUnicorns && !hasValuationData;
  const isValuationLoading = statsLoading;

  return (
    <div className='flex flex-col w-full h-full'>
      <div className='flex items-center justify-between gap-2 mb-3'>
        <h3 className='text-base font-medium sm:text-sm'>Tracked Unicorns</h3>
        <CustomTooltip
          trigger={
            <span className='-mt-2 -mr-2 text-white/40'>
              <Info className='size-5' />
            </span>
          }
          content='The count, combined value, and recent births all update based on your active filters. Combined value is the sum of latest known private valuations. Based on current HQ.'
        />
      </div>

      <div className='flex items-center gap-4'>
        {/* Donut Chart Section */}
        <div className='flex flex-col items-center'>
          <div className='relative size-[140px] sm:size-[150px]'>
            {loading && (
              <div className='absolute inset-0 z-20 flex items-center justify-center bg-[#161617]/50 backdrop-blur-sm transition-all duration-200 rounded-full'>
                <div className='w-6 h-6 border-3 border-white/10 border-t-[#FF9F43] rounded-full animate-spin'></div>
              </div>
            )}

            <div
              className={`absolute inset-0 transition-opacity duration-200 ${loading ? "opacity-50" : "opacity-100"}`}
            >
              <HighchartsReact
                highcharts={Highcharts}
                options={options}
                containerProps={{ style: { height: "100%", width: "100%" } }}
              />
            </div>

            <div className='absolute inset-0 z-10 text-center pointer-events-none'>
              <span className='absolute text-2xl font-bold leading-none -translate-x-1/2 -translate-y-6 sm:text-3xl sm:-translate-y-6 left-1/2 top-1/2'>
                {selectedCount.toLocaleString()}
              </span>
              <span className='absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-1.5 sm:translate-y-3.5 text-[9px] text-white/60 max-w-[100px] leading-tight'>
                in {selectionLabel}
              </span>
            </div>
          </div>
          <p
            className={`text-[10px] text-white/40 mt-1 transition-opacity duration-200 ${loading ? "opacity-50" : "opacity-100"}`}
          >
            {subtext || "Loading..."}
          </p>
        </div>

        {/* Divider */}
        <div className='w-px h-20 bg-white/10'></div>

        {/* Combined Valuation Section */}
        <div className='flex flex-col items-center justify-center flex-1 -my-2.5 text-center divide-y divide-white/10'>
          <div className='py-2.5'>
            <p className='text-[10px] text-white/40 mb-1'>
              Combined Valuation
            </p>
            {isValuationLoading ? (
              <div className='w-20 h-8 rounded bg-white/10 animate-pulse' />
            ) : showValuationNA ? (
              <>
                <p className='text-3xl font-bold'>N/A</p>
                <p className='text-[9px] text-white/40 mt-0.5'>
                  Data not available
                </p>
              </>
            ) : (
              <p className='text-3xl font-bold'>
                {totalValuation === 0 ? "$0" : formatBillionUSD(totalValuation)}
              </p>
            )}
          </div>
          <div className='py-2.5'>
            <p className='text-[10px] text-white/40 mb-1'>
              Unicorn Births <br /> (Last 12 Months)
            </p>
            {birthsLoading ? (
              <div className='w-20 h-8 rounded bg-white/10 animate-pulse' />
            ) : unicornBirths === null ? (
              <>
                <p className='text-3xl font-bold'>N/A</p>
                <p className='text-[9px] text-white/40 mt-0.5'>
                  Data not available
                </p>
              </>
            ) : (
              <p className='text-4xl font-bold'>{unicornBirths}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
